import { NgIf } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PAGE_ROUTES } from '../../constants/route.constant';
import { AuthService, HttpErrorResponse } from '../../services/auth.service';
import { SharedModule } from '../../shared/modules/shared.module';
import { LoadingComponent } from '../loading/loading.component';
import { map } from 'rxjs';
import { MigratedUserPromptComponent } from '../migrated-user-prompt/migrated-user-prompt.component';
import { extractErrors } from '../../utils/extracErrors.util';
import { MatSnackBar } from '@angular/material/snack-bar';

type CheckLoginMapType = {
  isExistedUsername: boolean;
  isExistedUser: boolean;
  isMigratedUser: boolean;
  username: string;
  email: string;
};

export enum PopupType {
  uM = 'userMigrated',
  uMWE = 'userMigratedWithoutEmail',
  unE = 'usernameExisted'
}
@Component({
  selector: 'app-login-by-user-password',
  imports: [
    MatFormField,
    ReactiveFormsModule,
    TranslatePipe,
    MatInput,
    LoadingComponent,
    MatIconButton,
    MatIcon,
    NgIf,
    MatSuffix,
    MatLabel,
    SharedModule,
    MigratedUserPromptComponent
  ],
  templateUrl: './login-by-user-password.component.html',
  standalone: true,
  styleUrl: './login-by-user-password.component.scss'
})
export class LoginByUserPasswordComponent implements OnInit, OnChanges {
  @Input() username?: string | null;

  protected form: FormGroup;
  protected isLoading = false;
  protected type?: PopupType = undefined;
  protected loginHint = '';
  protected promptContent = '';
  protected promptTitle = '';
  protected goPreStep(): void {
    this.loginHint = '';
  }
  protected readonly migratedPromptContent = 'Next time, use Email Address so you don’t have to sign in twice.';
  protected readonly migratedWithoutEmailPromptContent =
    'Your account is migrated, but your email is not found. Please contact us or try again with a different username and password.';
  protected readonly existedUsernamePromptContent =
    'We found your account, but the password you entered is incorrect. Click here to reset your password or try again with a different username and password.';
  protected readonly migratedPromptTitle = 'Account Migrated';
  protected readonly existedUsernamePromptTitle = 'Password Wrong';

  private _snackBar = inject(MatSnackBar);

  constructor(
    private _formBuilder: FormBuilder,
    private _authService: AuthService,
    private _router: Router,
    private _translate: TranslateService
  ) {
    this.form = this._formBuilder.group({
      username: ['', [Validators.required, Validators.minLength(1)]],
      password: ['', [Validators.required, Validators.minLength(1)]]
    });

    this.goPreStep = this.goPreStep.bind(this);
  }

  ngOnInit(): void {
    this._onUpdateInputs({
      username: this.username as string
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._onUpdateInputs({
      username: changes['username'].currentValue
    });
  }

  onSubmit = () => {
    if (this.form.valid) {
      this._handleCheckUsernameMigrated();
    }
  };

  showPassword = signal(false);

  clickEvent(event: MouseEvent) {
    this.showPassword.set(!this.showPassword());

    event.preventDefault();
    event.stopPropagation();
  }

  handleClickForgotPassword(): void {
    this._router.navigate([PAGE_ROUTES.FOTGOT_PASSWORD]);
  }

  private _handleCheckUsernameMigrated = () => {
    const password = this.form.value.password;
    const username = this.form.value.username;
    this.isLoading = true;

    this._authService
      .checkLogin(username, password)
      .pipe(
        map((result) => {
          return {
            isExistedUser: !!result?.userExists,
            isMigratedUser: !!result?.isUserMigrated,
            isExistedUsername: !!result?.usernameExists,
            username: result?.username ?? username,
            email: result?.email ?? ''
          };
        })
      )
      .subscribe({
        next: async (result: CheckLoginMapType) => {
          const { isExistedUser, isMigratedUser, isExistedUsername, username, email } = result;
          switch (true) {
            case isExistedUsername && !isExistedUser:
              this._onUsernameExisted(username);

              break;

            case !isExistedUser:
              this._onUserNotExisted(username);

              break;

            case isMigratedUser && !!email:
              this._onUserMigrated(email);

              break;

            case isMigratedUser && !email:
              this._onUserMigratedWithoutEmail(username);

              break;

            default:
              this._onUserNotMigrated(email, username, password);

              break;
          }
        },
        error: (error) => {
          this._extractErrors(error);
        }
      });
  };

  private _onUsernameExisted(username: string) {
    this.promptTitle = this.existedUsernamePromptTitle;
    this.promptContent = this.existedUsernamePromptContent;
    this.loginHint = username;
    this.isLoading = false;
    this.type = PopupType.unE;
  }

  private _onUserNotExisted(username: string) {
    this._onRedirectNotFound(username);
  }

  private _onUserMigrated(email: string) {
    this.promptTitle = this.migratedPromptTitle;
    this.promptContent = this.migratedPromptContent;
    this.loginHint = email;
    this.isLoading = false;
    this.type = PopupType.uM;
  }

  private _onUserMigratedWithoutEmail(username: string) {
    this.promptTitle = this.migratedPromptTitle;
    this.promptContent = this.migratedWithoutEmailPromptContent;
    this.loginHint = username;
    this.isLoading = false;
    this.type = PopupType.uMWE;
  }

  private _onUserNotMigrated(email: string, username: string, password: string) {
    this._router.navigate([PAGE_ROUTES.MIGRATE], { queryParams: { email }, state: { username: username, oldPassword: password } });
  }

  private _onRedirectNotFound(username: string) {
    this._router.navigate([PAGE_ROUTES.NOT_FOUND], {
      state: { returnTo: PAGE_ROUTES.HOME, queryParams: { email: username } }
    });
  }

  private _onUpdateInputs(newInputs: { username?: string; password?: string }) {
    this.form.patchValue(newInputs);
  }

  private _extractErrors(apiResponse: HttpErrorResponse): void {
    this.isLoading = false;
    const { status } = apiResponse ?? {};

    extractErrors({ status, snackBar: this._snackBar, translate: this._translate });
  }
}
